/**
 * Minimum-curvature directional survey math — pure, unit-tested, no React.
 *
 * The Surveys screen needs TVD & DLS at every station, but Oxbo-P leaves them
 * null for many runs. Rather than depend on those missing server values, we
 * integrate the raw (md / inc / azi) stations here, on the client, with the
 * standard minimum-curvature method. Depths are metres throughout (Olympic is
 * metric); DLS is normalised to the O&G convention of degrees per 30 m.
 *
 * References: the classic min-curvature equations (Sawaryn & Thorogood, and any
 * directional-drilling text). Given two adjacent stations 1->2 with measured
 * depths MD1, MD2 and inclination/azimuth (I1,A1),(I2,A2):
 *
 *   dMD = MD2 - MD1
 *   cos(b) = cos(I2-I1) - sin(I1)*sin(I2)*(1 - cos(A2-A1))      // dogleg angle b
 *   RF = (2/b)*tan(b/2)                                          // ratio factor, ->1 as b->0
 *   dTVD   = (dMD/2)*(cos I1 + cos I2)*RF
 *   dNorth = (dMD/2)*(sin I1*cos A1 + sin I2*cos A2)*RF
 *   dEast  = (dMD/2)*(sin I1*sin A1 + sin I2*sin A2)*RF
 *   DLS    = b * (30 / dMD)   in degrees per 30 m
 *
 * Vertical section here is the horizontal displacement (sqrt(N^2+E^2)); a fixed
 * section azimuth is not stored per pass, so the closure distance is the sane,
 * pass-independent choice for a Vsection-vs-TVD plot.
 */

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
/** DLS is quoted per this course length (metric O&G convention). */
export const DLS_COURSE_M = 30;

/** A raw survey station as it arrives from the API (subset we integrate on). */
export interface RawStation {
  md: number;
  inclination: number | null;
  azimuth: number | null;
}

/** A fully-integrated station: raw angles plus computed geometry, all metres. */
export interface ComputedStation {
  md: number;
  inclination: number;
  azimuth: number;
  /** True vertical depth, metres, accumulated from the first station. */
  tvd: number;
  /** +North offset from the tie-in, metres. */
  north: number;
  /** +East offset from the tie-in, metres. */
  east: number;
  /** Horizontal closure distance sqrt(N^2+E^2), metres — the vertical-section axis. */
  vsection: number;
  /** Dogleg severity at this station, degrees per 30 m (0 at the tie-in). */
  dls: number;
}

/**
 * Ratio factor RF = (2/b)*tan(b/2), with the b->0 limit handled (RF->1). b in rad.
 * For tiny b the guard avoids a 0/0; for straight holes RF is exactly 1.
 */
export function ratioFactor(betaRad: number): number {
  const b = Math.abs(betaRad);
  if (b < 1e-9) return 1; // straight / negligible dogleg
  return (2 / b) * Math.tan(b / 2);
}

/**
 * Dogleg angle b (radians) between two stations, clamped into a valid arccos
 * domain to absorb floating-point drift on near-collinear stations.
 */
export function doglegAngle(
  inc1Deg: number,
  azi1Deg: number,
  inc2Deg: number,
  azi2Deg: number,
): number {
  const i1 = inc1Deg * DEG;
  const i2 = inc2Deg * DEG;
  const dA = (azi2Deg - azi1Deg) * DEG;
  let cosB = Math.cos(i2 - i1) - Math.sin(i1) * Math.sin(i2) * (1 - Math.cos(dA));
  if (cosB > 1) cosB = 1;
  if (cosB < -1) cosB = -1;
  return Math.acos(cosB);
}

/**
 * Integrate a run of stations (MD-ascending) with minimum curvature.
 *
 * The first (shallowest) station is the tie-in: its TVD seeds from its own MD
 * assuming vertical above it (TVD = MD*cos I, i.e. a straight tie-on from
 * surface), N/E/Vsection = 0, DLS = 0. Every subsequent station accumulates
 * dTVD/dN/dE from its predecessor. Stations missing inc/azi are skipped for the
 * integration (a station can't contribute geometry without angles).
 *
 * Rows with MD <= 0 (e.g. a -30 m / 0 tie-on placeholder) are treated as the
 * tie-in with zero displacement so they don't inject spurious geometry.
 */
export function computeMinCurvature(stationsIn: RawStation[]): ComputedStation[] {
  // Keep only stations that can carry geometry, in strict MD order.
  const valid = stationsIn
    .filter(
      (s) =>
        Number.isFinite(s.md) &&
        s.inclination != null &&
        Number.isFinite(s.inclination) &&
        s.azimuth != null &&
        Number.isFinite(s.azimuth),
    )
    .map((s) => ({
      md: s.md,
      inclination: s.inclination as number,
      azimuth: s.azimuth as number,
    }))
    .sort((a, b) => a.md - b.md);

  const out: ComputedStation[] = [];
  const first = valid[0];
  if (first === undefined) return out;

  // Tie-in: straight from surface. MD<=0 placeholders anchor at the origin.
  const tieMd = Math.max(first.md, 0);
  out.push({
    md: first.md,
    inclination: first.inclination,
    azimuth: first.azimuth,
    tvd: tieMd * Math.cos(first.inclination * DEG),
    north: 0,
    east: 0,
    vsection: 0,
    dls: 0,
  });

  for (let i = 1; i < valid.length; i++) {
    const a = valid[i - 1];
    const b = valid[i];
    const prev = out[i - 1];
    if (a === undefined || b === undefined || prev === undefined) continue;

    const dMd = b.md - a.md;
    const beta = doglegAngle(a.inclination, a.azimuth, b.inclination, b.azimuth);
    const rf = ratioFactor(beta);

    const i1 = a.inclination * DEG;
    const i2 = b.inclination * DEG;
    const a1 = a.azimuth * DEG;
    const a2 = b.azimuth * DEG;
    const half = dMd / 2;

    const dTvd = half * (Math.cos(i1) + Math.cos(i2)) * rf;
    const dN = half * (Math.sin(i1) * Math.cos(a1) + Math.sin(i2) * Math.cos(a2)) * rf;
    const dE = half * (Math.sin(i1) * Math.sin(a1) + Math.sin(i2) * Math.sin(a2)) * rf;

    const north = prev.north + dN;
    const east = prev.east + dE;
    // DLS: dogleg per 30 m of course length. Guard a zero/duplicate MD step.
    const dls = dMd > 1e-9 ? beta * RAD * (DLS_COURSE_M / dMd) : 0;

    out.push({
      md: b.md,
      inclination: b.inclination,
      azimuth: b.azimuth,
      tvd: prev.tvd + dTvd,
      north,
      east,
      vsection: Math.hypot(north, east),
      dls,
    });
  }

  return out;
}

/** Axis-aligned bounds of a set of computed stations, for plot scaling. */
export interface Bounds {
  minN: number;
  maxN: number;
  minE: number;
  maxE: number;
  minTvd: number;
  maxTvd: number;
  minVs: number;
  maxVs: number;
}

/** Compute plot bounds; returns null for an empty run. */
export function computeBounds(pts: ComputedStation[]): Bounds | null {
  if (pts.length === 0) return null;
  const b: Bounds = {
    minN: Infinity,
    maxN: -Infinity,
    minE: Infinity,
    maxE: -Infinity,
    minTvd: Infinity,
    maxTvd: -Infinity,
    minVs: Infinity,
    maxVs: -Infinity,
  };
  for (const p of pts) {
    b.minN = Math.min(b.minN, p.north);
    b.maxN = Math.max(b.maxN, p.north);
    b.minE = Math.min(b.minE, p.east);
    b.maxE = Math.max(b.maxE, p.east);
    b.minTvd = Math.min(b.minTvd, p.tvd);
    b.maxTvd = Math.max(b.maxTvd, p.tvd);
    b.minVs = Math.min(b.minVs, p.vsection);
    b.maxVs = Math.max(b.maxVs, p.vsection);
  }
  return b;
}
